// /about — A manifesto. First-person plural. No SaaS. No safe language.
// Says out loud: we are hosts, this is what we saw break, this is what we are building,
// and we make no money from hosts. That last line is the whole point.
import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, MessageCircle } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const ACCENT = '#3a5a40';

const About = () => {
  const wa = () => window.open(
    'https://wa.me/919871155963?text=' + encodeURIComponent("Hi — read the GoHimalayan about page. I'd love to talk."),
    '_blank',
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-stone-800" data-testid="about-page">
      {/* ── Sticky nav ── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Mountain className="h-7 w-7" style={{ color: ACCENT }} strokeWidth={2.5} />
            <span className="text-lg font-bold tracking-tight text-stone-900" style={pf}>GoHimalayan</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/host" className="hidden sm:inline px-4 py-1.5 text-xs tracking-wider uppercase border border-stone-300 rounded-full text-stone-700 hover:bg-stone-100 transition" data-testid="about-host-cta">Host with us</Link>
            <Link to="/" className="text-xs tracking-wider uppercase text-stone-500 hover:text-stone-900">Home</Link>
          </div>
        </div>
      </header>

      {/* ── Hero — the confession ── */}
      <section className="pt-32 pb-20 px-6 lg:px-8" data-testid="about-hero">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.4em] uppercase font-semibold mb-6" style={{ color: ACCENT }}>Why we built this</p>
          <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-stone-900 leading-[1.1] mb-8" style={pf}>
            We didn't set out to build a platform.
            <br />
            <span className="italic" style={{ color: ACCENT }}>We started a homestay.</span>
          </h1>
          <p className="text-stone-600 text-lg md:text-[19px] leading-[1.85]" style={pf}>
            On a quiet bend of the Tirthan river, in a corner of the Indian Himalayas, we built <em>Bluehouse by the River</em>. We chose every cedar beam. We learned what guests really wanted. We watched what happens when an independent host with a great story has to compete on aggregators built for scale.
          </p>
        </div>
      </section>

      {/* ── What we saw break ── */}
      <section className="pb-20 px-6 lg:px-8" data-testid="about-broken">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 font-semibold mb-3">What we saw break</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-8" style={pf}>
            The OTAs don't reward distinctiveness. They reward discounting.
          </h2>
          <div className="space-y-6 text-stone-600 text-base md:text-[17px] leading-[1.9]" style={pf}>
            <p>
              We watched independent hosts — people who built something <em>real</em> — disappear inside marketplaces that rank by discount and commission, not by story or stewardship. 18-22% of every booking, vanished. The guest never knew the host's name.
            </p>
            <p>
              We watched discerning travellers — families, couples, solo explorers from across the world — give up after page three of Booking.com, frustrated that they couldn't speak to the actual host before committing to a stay. They wanted to ask about the food. About the dog. About whether the trail starts from the back garden.
            </p>
            <p>
              We watched the Himalayas get reduced to two months a year. A summer rush in May and June, ghost-town silence for the other ten. The mountains are a 365-day continent. Booking systems weren't built to tell that story.
            </p>
            <p>
              And we watched yoga collectives, cycling crews, art residencies, writing retreats and corporate offsites scramble for trusted Himalayan partners — finding none who knew the valleys properly.
            </p>
            <p className="text-stone-900 font-medium pt-2">
              The whole system was solving for the wrong thing.
            </p>
          </div>
        </div>
      </section>

      {/* ── What we're building ── */}
      <section className="py-20 px-6 lg:px-8 bg-white border-y border-stone-100" data-testid="about-building">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.35em] uppercase font-semibold mb-3" style={{ color: ACCENT }}>What we're building</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-8" style={pf}>
            An editorial commons for Himalayan hospitality.
          </h2>
          <div className="space-y-6 text-stone-600 text-base md:text-[17px] leading-[1.9]" style={pf}>
            <p>
              GoHimalayan is a hand-picked collection of <em>experiential</em> stays across the Himalayan arc — India, Nepal, Bhutan, Tibet. Cottages on river bends. Heritage manors at 9,000 feet. Cedar lodges run by families who've stewarded the same hillside for three generations.
            </p>
            <p>
              Every stay on this platform is here because we believe in it. We write its story. We curate its imagery. We connect the traveller directly to the host — phone number, WhatsApp, name. Real conversations between humans before money changes hands.
            </p>
            <p>
              And we do not earn a single rupee from the host. <em>Not in commission. Not in listing fees. Not now, not in the foreseeable future.</em> If we ever do introduce a paid offering, the original 100 hosts on this platform are grandfathered, forever. That is a promise we put in writing.
            </p>
          </div>

          {/* The numbers grid */}
          <div className="grid grid-cols-3 gap-4 mt-12 pt-10 border-t border-stone-100">
            {[
              { v: '0%', l: 'commission · ever' },
              { v: '0', l: 'listing fee · ever' },
              { v: '100%', l: 'of every booking · to the host' },
            ].map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-stone-900" style={pf}>{m.v}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-stone-400 mt-2">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hosts in good company ── */}
      <section className="py-20 px-6 lg:px-8" data-testid="about-good-company">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 font-semibold mb-3">A platform of peers</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-8" style={pf}>
            If you're a host on GoHimalayan, you're in good company.
          </h2>
          <div className="space-y-6 text-stone-600 text-base md:text-[17px] leading-[1.9]" style={pf}>
            <p>
              We don't list resorts. We don't list chains. We don't list anything that doesn't feel built by hand and run with love. Every property here has been chosen because it adds something singular to the mountains — a view, a kitchen, a host's voice, a way of doing things you can't quantify.
            </p>
            <p>
              As GoHimalayan grows, the platform grows for every host on it. New traveller cohorts. Community partnerships. Corporate retreats that fill rooms midweek and through the quiet months. State tourism partnerships. The Himalayan brand of hospitality has a global resonance — and we'd like the world to come stay with the people who actually live here.
            </p>
            <p className="text-stone-900 font-medium pt-1">
              That's the long game. The world needs to experience the Himalayas. We're building the door.
            </p>
          </div>
        </div>
      </section>

      {/* ── The 365-day belief ── */}
      <section className="py-20 px-6 lg:px-8 bg-[#1a2a1c] text-stone-200" data-testid="about-365">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.35em] uppercase text-amber-200/70 font-semibold mb-4">Our north star</p>
          <h2 className="text-3xl sm:text-4xl font-light italic leading-[1.25] mb-8" style={pf}>
            "A Himalayas open every day of the year — to travellers who arrive on their own terms, hosted by the people who never left."
          </h2>
          <div className="w-12 h-[1px] bg-amber-200/30 mb-8" />
          <div className="space-y-5 text-stone-300 text-[17px] leading-[1.9]" style={pf}>
            <p>Spring blossoms in March. Monsoon mist in July. Apple-harvest autumn in October. The lit hush of January snow. Every season has its travellers. Every valley has its season.</p>
            <p>We're building so the right traveller finds the right host in the right month. Not just May–June. <em>All twelve.</em></p>
          </div>
        </div>
      </section>

      {/* ── How we'll eventually make money (the honest bit) ── */}
      <section className="py-20 px-6 lg:px-8" data-testid="about-revenue">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 font-semibold mb-3">The honest bit</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-8" style={pf}>
            "So how does GoHimalayan ever make money?"
          </h2>
          <div className="space-y-6 text-stone-600 text-base md:text-[17px] leading-[1.9]" style={pf}>
            <p>
              Not from the host. That's the part we want to be unambiguous about. The discovery layer, the booking layer, the storytelling layer — free, forever, for the 100 hosts who join us first. We will absorb that cost ourselves.
            </p>
            <p>
              Where we believe revenue can fairly come from later: <strong className="text-stone-800">community group bookings</strong> (yoga, cycling, art collectives at scale), <strong className="text-stone-800">corporate retreat curation</strong>, <strong className="text-stone-800">state tourism partnerships</strong>, and eventually a <strong className="text-stone-800">strategic investor</strong> who shares the vision of a Himalayan hospitality network that the OTAs structurally cannot replicate.
            </p>
            <p>
              We're investing our own money to get there. Slowly, deliberately, one valley at a time. We're in no hurry. We'd rather grow a hundred deep relationships in two years than ten thousand shallow ones in two months.
            </p>
            <p className="text-stone-900 font-medium pt-1">
              We are, after all, hosts ourselves. We know what it takes to do this right.
            </p>
          </div>
        </div>
      </section>

      {/* ── CTAs ── */}
      <section className="pb-24 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl border border-stone-200 p-10 md:p-12 text-center shadow-sm">
            <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 font-semibold mb-3">If this resonates</p>
            <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-5" style={pf}>Come build it with us.</h3>
            <p className="text-stone-600 text-base leading-[1.85] max-w-xl mx-auto mb-8" style={pf}>
              If you run an experiential Himalayan stay — or you'd like to come stay at one — we'd love to talk. No pressure. No sales call. Just a conversation between people who care about the mountains.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/host" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold text-white transition" style={{ background: ACCENT }} data-testid="about-cta-host">
                Host with us<ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={wa} className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold border border-stone-300 text-stone-800 hover:bg-stone-100 transition" data-testid="about-cta-wa">
                <MessageCircle className="w-4 h-4" />Talk to us
              </button>
            </div>
            <p className="text-[11px] text-stone-400 mt-6">
              Communities & retreats:{' '}
              <a href="https://wa.me/919871155963?text=Hi%20—%20we're%20a%20community%2Fretreat%20group%20looking%20to%20partner%20with%20GoHimalayan." target="_blank" rel="noopener noreferrer" className="underline" data-testid="about-cta-communities">
                WhatsApp us
              </a>
            </p>
          </div>

          {/* Footer note */}
          <p className="text-[12px] text-stone-400 mt-10 text-center leading-relaxed" style={pf}>
            Written from a homestay on the Tirthan, edited from a balcony in the Kullu Valley.<br />
            Founders & hosts · GoHimalayan
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-stone-200 py-8 px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mountain className="h-5 w-5" style={{ color: ACCENT }} />
            <span className="text-sm text-stone-500">© 2026 GoHimalayan</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-stone-400">
            <Link to="/host" className="hover:text-stone-700">Host with us</Link>
            <Link to="/editorial-policy" className="hover:text-stone-700">Editorial policy</Link>
            <Link to="/privacy" className="hover:text-stone-700">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-700">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
